<template>
  <fieldset
    class="chart"
    :class="{ paired, coloured: colour !== null, many: bars.length > FEW }"
    :style="colour === null ? undefined : { '--bar-colour': colour }"
  >
    <legend class="unseen">{{ legend }}</legend>
    <div class="reading"><slot /></div>
    <div
      ref="area"
      class="area"
      :class="{ short }"
      @pointerdown="pointer.down"
      @pointermove="pointer.move"
      @pointerup="pointer.up"
      @pointercancel="pointer.cancel"
    >
      <span
        v-if="average !== null"
        class="average"
        :style="{ height: heightOf(average) }"
        aria-hidden="true"
      ></span>
      <template v-for="(bar, index) in bars" :key="bar.key">
        <!-- A month before the first with data or still to come: a label, no bar, no choice. -->
        <span v-if="bar.quiet" class="bar quiet" aria-hidden="true"></span>
        <label v-else class="bar" :class="{ chosen: index === modelValue }">
          <input
            type="radio"
            class="radio"
            :name="name"
            :value="index"
            :checked="index === modelValue"
            :aria-label="bar.spoken"
            @change="choose(index)"
          />
          <span
            v-if="paired"
            class="fill outline"
            :style="{ height: heightOf(bar.outline ?? 0) }"
            aria-hidden="true"
          ></span>
          <span
            class="fill"
            :class="{ unknown: bar.level === null }"
            :style="{ height: bar.level === null ? '100%' : heightOf(bar.level) }"
            aria-hidden="true"
          ></span>
        </label>
      </template>
    </div>
    <div class="labels" aria-hidden="true">
      <span
        v-for="(bar, index) in bars"
        :key="bar.key"
        class="label"
        :class="{ chosen: index === modelValue }"
      >
        <span class="word">{{ bar.label }}</span
        ><span v-if="bars.length > FEW" class="letter">{{
          bar.label.charAt(0).toLocaleUpperCase()
        }}</span>
      </span>
    </div>
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, ref, useId } from 'vue'
import type { PropType } from 'vue'
import { CHART_LEVEL } from '@molvia/model'
import { useChartPointer } from '@/composables/useChartPointer'

export interface ChartBar {
  /** The month, `YYYY-MM`. */
  readonly key: string
  /** Under the bar: «апр». */
  readonly label: string
  /** What the bar says to a screen reader: «Август 2026, 345 620 ֏». */
  readonly spoken: string
  /**
   * Its height in thousandths of the tallest — the server's (`CHART_LEVEL`). Null is «not known» —
   * «Ушло» of a month with no rate — drawn as a dashed empty bar, never as nothing spent (review).
   */
  readonly level: number | null
  /** The bar before it in a pair, drawn as an outline — «Пришло» beside «Ушло». */
  readonly outline?: number | null
  /**
   * A month with no bar (MOL-160, handoff MOL-157 04): before the first with data, or still to
   * come — its label stays, and there is nothing to choose. Not `level` null, which is «not known».
   */
  readonly quiet?: boolean
}

/** Six bars and fewer stand apart; twelve stand close (handoff 03). */
const FEW = 6

/**
 * The bars of «Графики» (MOL-74, handoff 03) — one component for the three bar charts: a reading
 * above the bars instead of a tip under the finger, and the whole area as the target, so a finger
 * picks the bar it is over and drags along them (`useChartPointer`; `touch-action: pan-y` leaves the
 * page its scroll).
 * The bars are radios: arrows move the choice and every bar is read by its month and amount — which
 * is why the reading is not a live region: the radio already says it, and a drag would chatter.
 * HTML and tokens, no library (Р-1): the scheme changes by itself and the words are text.
 */
export default defineComponent({
  name: 'BarChart',
  props: {
    bars: { type: Array as PropType<readonly ChartBar[]>, required: true },
    modelValue: { type: Number, required: true },
    legend: { type: String, required: true },
    /** Bars in pairs: `outline` outlined, `level` filled. */
    paired: { type: Boolean, default: false },
    /** One colour for every bar, the chosen one full — a category's (handoff 03). */
    colour: { type: String as PropType<string | null>, default: null },
    /** A dashed line at this level, drawn over the bars — the average (handoff MOL-157 04). */
    average: { type: Number as PropType<number | null>, default: null },
    /** 120 px of bars rather than 150 (handoff 03, «Категория во времени»). */
    short: { type: Boolean, default: false },
  },
  emits: {
    'update:modelValue': (index: number) => Number.isInteger(index),
  },
  setup(props, { emit }) {
    const area = ref<HTMLElement | null>(null)

    function choose(index: number): void {
      if (index !== props.modelValue) emit('update:modelValue', index)
    }

    /** The bar under the pointer: the area is cut into as many columns as there are bars. */
    const pointer = useChartPointer(area, (fraction) => {
      const n = props.bars.length
      const index = Math.min(n - 1, Math.floor(fraction * n))
      if (n > 0 && !props.bars[index]?.quiet) choose(index)
    })

    const heightOf = (level: number) => `${String((level * 100) / CHART_LEVEL)}%`

    return { FEW, area, name: useId(), choose, pointer, heightOf }
  },
})
</script>

<style scoped lang="scss">
.chart {
  --bar-colour: var(--accent);

  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
}

.unseen,
.radio {
  @include visually-hidden;
}

.reading {
  min-height: 4.875rem;
}

/* A stacking context of its own: the average's z-index lifts it over the bars, and without one it
   rose over the screen's pinned header too, drawn through «‹ Деньги» as the page scrolled under it. */
.area {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  height: 9.375rem;
  margin-top: var(--space-3);
  touch-action: pan-y;

  &.short {
    height: 7.5rem;
  }

  .many & {
    gap: var(--space-1);
  }
}

.average {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: 0;
  left: 0;
  border-top: 2px dashed var(--text);
  pointer-events: none;
  transition: height var(--dur) var(--ease);
}

.bar {
  position: relative;
  display: flex;
  flex: 1;
  align-items: flex-end;
  justify-content: center;
  gap: var(--space-1);
  height: 100%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:has(.radio:focus-visible) {
    @include focus-ring;

    border-radius: var(--radius-sm);
  }

  &.quiet {
    cursor: default;
  }
}

.fill {
  width: 100%;
  max-width: 2.25rem;
  min-height: 2px;
  border-radius: var(--radius-sm) var(--radius-sm) var(--radius-mark) var(--radius-mark);
  background: var(--graphic);

  /* A new answer — another period, another category — grows the bars where they stand (MOL-151):
     the area keeps its height, so nothing around it moves. */
  transition:
    background-color var(--dur-fast) var(--ease-out),
    height var(--dur) var(--ease);

  .chosen & {
    background: var(--bar-colour);
  }

  .coloured & {
    background: var(--bar-colour);
    opacity: 0.42;
  }

  .coloured .chosen & {
    opacity: 1;
  }
}

.fill.unknown {
  background: none;
  box-shadow: none;
  outline: 1.5px dashed var(--graphic);
  outline-offset: -1.5px;
  opacity: 1;
}

.paired .fill {
  max-width: 1.25rem;
  border-radius: calc(var(--radius-sm) - var(--radius-mark))
    calc(var(--radius-sm) - var(--radius-mark)) var(--radius-mark) var(--radius-mark);
}

.outline {
  background: none;
  box-shadow: inset 0 0 0 2px var(--graphic);

  .chosen & {
    background: none;
    box-shadow: inset 0 0 0 2px var(--text);
  }
}

.labels {
  /* It measures itself for the months' letters below (MOL-321). */
  container-type: inline-size;
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-1);

  .many & {
    gap: var(--space-1);
  }
}

.letter {
  display: none;
}

/* Twelve months narrower than this are named by their letter, every other month's, over its own column and the
   empty one beside — as a phone's year chart names them (MOL-321): at 200 % a label had 9 px, «янв» showed half its
   «я», and even a letter alone is 20 px. The row is 16rem at 100 % on 320 and keeps the words. */
@container (width < 14rem) {
  .many .label {
    overflow: visible;
  }

  .many .word {
    display: none;
  }

  .many .label:nth-child(odd) .letter {
    display: inline;
  }

  /* The chosen month is named whatever its place, its neighbours' letters giving it room (adversarial К3): October
     chosen, under the bars stood «Я М М И С Н» and nothing of it — the label's ink is the chosen month's second sign. */
  .many .label.chosen .letter {
    display: inline;
  }

  .many .label:has(+ .label.chosen) .letter,
  .many .label.chosen + .label .letter {
    display: none;
  }
}

.label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--text-muted);
  font-size: var(--text-caption);
  font-weight: var(--weight-medium);
  text-align: center;
  white-space: nowrap;

  /* Chosen is the bar's fill; the label only darkens, at the same weight (MOL-179, MOL-186 Р-2). */
  &.chosen {
    color: var(--text);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fill,
  .average {
    transition: none;
  }
}
</style>
